import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  ArrowLeft, 
  ChevronDown, 
  QrCode, 
  Bell, 
  Users, 
  CreditCard, 
  Smartphone, 
  Mail, 
  Shield, 
  HelpCircle,
  Play,
  MessageCircle
} from 'lucide-react';
import { resetOnboarding } from '../components/Onboarding';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';

// ── Dados do FAQ ─────────────────────────────────────────────────────────────
const faqCategories = [
  {
    id: 'qrcode',
    title: 'QR Code',
    icon: QrCode,
    color: 'bg-blue-100 text-blue-600',
    questions: [
      {
        q: 'Como funciona o QR Code do Lembrou?',
        a: 'Cada profissional/estabelecimento tem um QR Code exclusivo. Quando seu cliente escaneia esse código com o celular, ele é direcionado para uma página de cadastro onde pode se registrar para receber lembretes automáticos sobre seus serviços. É uma forma prática de captar clientes sem precisar anotar dados manualmente!'
      },
      {
        q: 'Onde devo colocar meu QR Code?',
        a: 'Recomendamos imprimir e colocar em locais de fácil visualização: balcão de atendimento, porta de entrada, cartões de visita, recibos, ou qualquer material que o cliente veja. Quanto mais visível, mais clientes vão se cadastrar!'
      },
      {
        q: 'Como imprimir meu QR Code?',
        a: 'No seu Dashboard, clique no botão "Exportar QR Code" no canto superior direito. Uma página será aberta com seu QR Code em alta qualidade, pronto para impressão. Você pode imprimir em qualquer tamanho!'
      },
      {
        q: 'Meu QR Code é único?',
        a: 'Sim! Cada conta do Lembrou tem um QR Code exclusivo vinculado ao seu estabelecimento. Todos os clientes que escanearem seu código serão automaticamente associados à sua conta.'
      },
    ]
  },
  {
    id: 'clientes',
    title: 'Clientes e Cadastros',
    icon: Users,
    color: 'bg-green-100 text-green-600',
    questions: [
      {
        q: 'Como meus clientes se cadastram?',
        a: 'Existem duas formas: 1) O cliente escaneia seu QR Code e preenche o cadastro automaticamente, ou 2) Você cadastra manualmente pelo Dashboard clicando em "Novo Lembrete" e preenchendo os dados do cliente.'
      },
      {
        q: 'Quantos clientes posso cadastrar?',
        a: 'No plano FREE você pode ter até 20 lembretes ativos. No plano PRO, os lembretes são ilimitados! Além disso, o PRO inclui notificações por WhatsApp.'
      },
      {
        q: 'O cliente precisa ter conta no Lembrou?',
        a: 'Não! O cliente só precisa fornecer nome, email ou telefone. Ele receberá os lembretes automaticamente sem precisar criar conta ou instalar nada.'
      },
      {
        q: 'Posso editar os dados de um cliente?',
        a: 'Sim! No Dashboard, encontre o lembrete do cliente e clique em "Editar". Você pode alterar nome, contato, serviço e data de vencimento a qualquer momento.'
      },
    ]
  },
  {
    id: 'notificacoes',
    title: 'Notificações e Lembretes',
    icon: Bell,
    color: 'bg-orange-100 text-orange-600',
    questions: [
      {
        q: 'Quando as notificações são enviadas?',
        a: 'O Lembrou envia notificações automáticas em 3 momentos: 15 dias antes (D-15), 7 dias antes (D-7) e no dia do vencimento (D-0). Isso garante que seu cliente tenha tempo de se preparar!'
      },
      {
        q: 'Por quais canais as notificações são enviadas?',
        a: 'No plano FREE, as notificações são enviadas por Email. No plano PRO, você também pode enviar por WhatsApp, que tem uma taxa de abertura muito maior!'
      },
      {
        q: 'Posso personalizar a mensagem de notificação?',
        a: 'Ao enviar manualmente pelo Dashboard, você pode editar a mensagem antes de enviar. As notificações automáticas usam um template padrão que inclui o nome do cliente, serviço e data.'
      },
      {
        q: 'O que acontece se o cliente não tiver email ou telefone?',
        a: 'O sistema só envia notificações para os canais que possuem dados cadastrados. Se não tiver email, não envia email. Se não tiver telefone (e for PRO), não envia WhatsApp. Recomendamos sempre cadastrar pelo menos um contato!'
      },
      {
        q: 'Posso enviar uma notificação manualmente?',
        a: 'Sim! No Dashboard, clique no botão "WhatsApp" ao lado do lembrete para enviar uma mensagem manual. Você pode personalizar o texto antes de enviar.'
      },
    ]
  },
  {
    id: 'planos',
    title: 'Planos e Pagamentos',
    icon: CreditCard,
    color: 'bg-purple-100 text-purple-600',
    questions: [
      {
        q: 'Qual a diferença entre FREE e PRO?',
        a: 'FREE: até 20 lembretes, notificações por email, QR Code e painel de controle. PRO (R$19,99/mês): lembretes ilimitados, notificações por email E WhatsApp, importação CSV/PDF, e suporte prioritário.'
      },
      {
        q: 'Como faço para assinar o plano PRO?',
        a: 'No Dashboard, clique no card do seu plano ou vá em "Perfil" e clique em "Assinar PRO". Você será redirecionado para uma página de pagamento segura do Stripe.'
      },
      {
        q: 'Posso cancelar a assinatura a qualquer momento?',
        a: 'Sim! Você pode cancelar quando quiser pelo seu Perfil. A assinatura permanece ativa até o final do período pago, e depois você volta automaticamente para o plano FREE.'
      },
      {
        q: 'Quais formas de pagamento são aceitas?',
        a: 'Aceitamos cartões de crédito (Visa, Mastercard, American Express, etc.) através do Stripe, uma das plataformas de pagamento mais seguras do mundo.'
      },
      {
        q: 'Existe cupom de desconto?',
        a: 'Sim! Eventualmente oferecemos cupons promocionais. Na página de pagamento, você pode inserir o código do cupom para obter desconto na assinatura.'
      },
    ]
  },
  {
    id: 'whatsapp',
    title: 'WhatsApp',
    icon: MessageCircle,
    color: 'bg-green-100 text-green-700',
    questions: [
      {
        q: 'Como funciona o envio por WhatsApp?',
        a: 'As mensagens são enviadas através do número oficial do Lembrou. Seu cliente recebe a mensagem com seu nome/empresa, o serviço e a data de vencimento. Funciona como se fosse uma notificação profissional!'
      },
      {
        q: 'O WhatsApp é gratuito?',
        a: 'O envio de WhatsApp está disponível apenas no plano PRO (R$19,99/mês). No plano FREE, as notificações são enviadas apenas por email.'
      },
      {
        q: 'Meu número de WhatsApp aparece para o cliente?',
        a: 'Não. As mensagens são enviadas pelo número do Lembrou. A mensagem identifica seu estabelecimento pelo nome cadastrado, mas seu número pessoal fica protegido.'
      },
      {
        q: 'Como sei se o WhatsApp está funcionando?',
        a: 'No Dashboard, você verá o indicador "WhatsApp OK" em verde se tudo estiver funcionando. Se houver problema, aparecerá um alerta.'
      },
    ]
  },
  {
    id: 'seguranca',
    title: 'Segurança e Privacidade',
    icon: Shield,
    color: 'bg-red-100 text-red-600',
    questions: [
      {
        q: 'Meus dados estão seguros?',
        a: 'Sim! Utilizamos criptografia de ponta a ponta, servidores seguros e seguimos as melhores práticas de segurança. Seus dados e de seus clientes estão protegidos.'
      },
      {
        q: 'Os dados dos meus clientes são compartilhados?',
        a: 'Nunca! Os dados dos seus clientes são exclusivamente seus. Não vendemos, compartilhamos ou usamos para qualquer outra finalidade além de enviar as notificações que você configurou.'
      },
      {
        q: 'Posso excluir dados de um cliente?',
        a: 'Sim! Você pode excluir qualquer lembrete/cliente a qualquer momento pelo Dashboard. Os dados serão permanentemente removidos do sistema.'
      },
    ]
  },
];

// ── Componente de Item do FAQ (Accordion) ────────────────────────────────────
const FaqItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border-b border-gray-100 last:border-0">
    <button
      onClick={onClick}
      className="w-full py-4 px-1 flex items-center justify-between text-left hover:bg-gray-50/50 transition-colors rounded-lg"
    >
      <span className="font-medium text-gray-800 pr-4">{question}</span>
      <ChevronDown 
        className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${
          isOpen ? 'rotate-180' : ''
        }`} 
      />
    </button>
    <div 
      className={`overflow-hidden transition-all duration-300 ease-in-out ${
        isOpen ? 'max-h-96 pb-4' : 'max-h-0'
      }`}
    >
      <p className="text-gray-600 leading-relaxed px-1">{answer}</p>
    </div>
  </div>
);

// ── Componente de Categoria do FAQ ───────────────────────────────────────────
const FaqCategory = ({ category, openQuestions, toggleQuestion }) => {
  const Icon = category.icon;
  return (
    <Card className="rounded-2xl border shadow-sm overflow-hidden">
      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-10 h-10 rounded-xl ${category.color} flex items-center justify-center`}>
            <Icon className="w-5 h-5" />
          </div>
          <h2 className="text-lg font-bold text-gray-900">{category.title}</h2>
        </div>
        <div className="space-y-1">
          {category.questions.map((item, index) => (
            <FaqItem
              key={index}
              question={item.q}
              answer={item.a}
              isOpen={openQuestions[`${category.id}-${index}`]}
              onClick={() => toggleQuestion(`${category.id}-${index}`)}
            />
          ))}
        </div>
      </div>
    </Card>
  );
};

// ── Página Principal do FAQ ──────────────────────────────────────────────────
const FAQ = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openQuestions, setOpenQuestions] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  // ── Detecta de onde o usuário veio ──────────────────────────────────────────
  // Se veio do dashboard (state: { fromDashboard: true }) → header interno
  // Se chegou diretamente pela URL /faq                   → Navbar público
  const fromDashboard = location.state?.fromDashboard === true;

  const toggleQuestion = (key) => {
    setOpenQuestions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleReplayOnboarding = () => {
    resetOnboarding();
    toast.success('Tour reiniciado! Volte ao Dashboard para ver.');
    navigate('/dashboard');
  };

  const filteredCategories = searchTerm
    ? faqCategories.map(cat => ({
        ...cat,
        questions: cat.questions.filter(
          q => q.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
               q.a.toLowerCase().includes(searchTerm.toLowerCase())
        )
      })).filter(cat => cat.questions.length > 0)
    : faqCategories;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">

      {/* ── HEADER ────────────────────────────────────────────────────────────
           fromDashboard = true  → header compacto com "Voltar" e "Rever Tour"
           fromDashboard = false → Navbar público com links e "Começar Grátis"
          ──────────────────────────────────────────────────────────────────── */}
      {fromDashboard ? (
        <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <Button
                onClick={() => navigate(-1)}
                variant="ghost"
                className="rounded-xl"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
              <Button
                onClick={handleReplayOnboarding}
                variant="outline"
                className="rounded-xl text-primary border-primary/30 hover:bg-primary/5"
              >
                <Play className="w-4 h-4 mr-2" />
                Rever Tour Inicial
              </Button>
            </div>
          </div>
        </header>
      ) : (
        <Navbar />
      )}

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <HelpCircle className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            Central de Ajuda
          </h1>
          <p className="text-gray-500 max-w-xl mx-auto">
            Encontre respostas para as dúvidas mais frequentes sobre o Lembrou
          </p>
        </div>

        {/* Busca */}
        <div className="mb-8">
          <div className="relative max-w-xl mx-auto">
            <input
              type="text"
              placeholder="Buscar pergunta..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-5 py-3.5 pl-12 rounded-2xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-gray-800"
            />
            <svg 
              className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        {/* Categorias */}
        <div className="space-y-6">
          {filteredCategories.length > 0 ? (
            filteredCategories.map(category => (
              <FaqCategory
                key={category.id}
                category={category}
                openQuestions={openQuestions}
                toggleQuestion={toggleQuestion}
              />
            ))
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <HelpCircle className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500">Nenhuma pergunta encontrada para "{searchTerm}"</p>
              <Button
                onClick={() => setSearchTerm('')}
                variant="ghost"
                className="mt-4 text-primary"
              >
                Limpar busca
              </Button>
            </div>
          )}
        </div>

        {/* CTA de contato */}
        <Card className="mt-10 rounded-2xl border shadow-sm bg-gradient-to-br from-primary/5 to-transparent p-8 text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Ainda tem dúvidas?
          </h3>
          <p className="text-gray-500 mb-6">
            Nossa equipe está pronta para ajudar você
          </p>
          <Button
            onClick={() => window.open('mailto:suporte@lembrou.com.br', '_blank')}
            variant="outline"
            className="rounded-xl"
          >
            <Mail className="w-4 h-4 mr-2" />
            suporte@lembrou.com.br
          </Button>
        </Card>
      </main>

      <footer className="border-t border-gray-100 mt-12 py-6">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm text-gray-400">
          © 2026 Lembrou — Todos os direitos reservados
        </div>
      </footer>
    </div>
  );
};

export default FAQ;
